import React, { useEffect, useState } from "react";
import { ResponsiveContainer, ComposedChart, LineChart, BarChart, Bar, Line, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { api } from "@/lib/api";
import { arDigits, fixArThousands } from "@/lib/utils";
import YieldMethodologyInfo from "@/components/common/YieldMethodologyInfo";
import { Info } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { fmtAED, fmtMonth, fmtPct, fmtPsf, toCur, N } from "@/components/market/intel/intel";
import RoundSelect from "@/components/market/intel/RoundSelect";

export const COL = { A: "#0A2540", B: "#B08543" };
const AX = { fontSize: 10, fill: "#6B665D" };
const BAND_IDS = ["under_1m", "1m_2m", "2m_5m", "5m_10m", "over_10m"];
const BAND_LBL = { en: ["< 1M", "1–2M", "2–5M", "5–10M", "10M+"], ar: ["< ١ مليون", "١–٢ مليون", "٢–٥ ملايين", "٥–١٠ ملايين", "+١٠ ملايين"] };

export const CT = {
  en: {
    higher: "Higher of the two", psf: "2026 YTD median-based price / sq ft", psfTip: "Based on the 2026 year-to-date median price per sq ft of registered sales, all property types. Where an area combines several DLD sub-areas, the figure is a sales-weighted average of the sub-area medians, not a single pooled median. A median and an average are different measures, so this figure can differ from the monthly charts below.", yield: "Gross yield", yoy: "YoY change", rent1: "1BR rent / yr", rent2: "2BR rent / yr", txns: "DLD registered sales 2026 YTD",
    trend: "Monthly median price / sq ft — registered sales", trendSub: "All property types · the DLD sub-area with the most sales in each curated area · the latest month may still be in progress",
    sales: "Monthly registered sales", salesSub: "Pure sales count per month, complete months",
    bands: "Where the sales money goes", bandsSub: (m) => `Share of sales by transaction price · ${m}`,
    move: "Price movement & confidence", moveSub: (m) => `Monthly median price / sq ft · Units only · all DLD sub-areas combined · complete months to ${m}`,
    median: "Median", sample: (n) => `${n} sales`, conf: { larger: "Larger sample", limited: "Limited sample", insufficient: "Insufficient data" },
    month: "Month", src: "Source: Dubai Land Department · registered pure sales", approx: "≈ approximate value for an area not yet matched by the live DLD aggregate.",
    loading: "Loading…", na: "Data temporarily unavailable.",
  },
  ar: {
    higher: "الأعلى بين المنطقتين", psf: "سعر القدم المربع منذ بداية ٢٠٢٦ (مبني على الوسيط)", psfTip: "وسيط سعر القدم المربع للمبيعات المسجّلة منذ بداية عام ٢٠٢٦، لجميع أنواع العقارات. عندما تضمّ المنطقة عدّة مناطق فرعية لدى الدائرة، يكون الرقم متوسطاً مرجّحاً بعدد المبيعات لقيم الوسيط في المناطق الفرعية، وليس وسيطاً مُجمّعاً واحداً. الوسيط والمتوسط مقياسان مختلفان، لذا قد يختلف هذا الرقم عن الرسوم الشهرية أدناه.", yield: "العائد الإجمالي", yoy: "التغيّر السنوي", rent1: "إيجار غرفة نوم / سنوياً", rent2: "إيجار غرفتين / سنوياً", txns: "المبيعات المسجّلة لدى الدائرة ٢٠٢٦ حتى الآن",
    trend: "الوسيط الشهري لسعر القدم المربع — المبيعات المسجّلة", trendSub: "جميع أنواع العقارات · المنطقة الفرعية الأكثر مبيعات في كل منطقة · قد يكون الشهر الأخير قيد التحديث",
    sales: "المبيعات المسجّلة شهرياً", salesSub: "عدد صفقات البيع في الأشهر المكتملة",
    bands: "أين تذهب أموال المبيعات", bandsSub: (m) => `توزيع المبيعات حسب سعر الصفقة · ${m}`,
    move: "حركة الأسعار ودرجة الثقة", moveSub: (m) => `الوسيط الشهري لسعر القدم المربع · الوحدات فقط · جميع المناطق الفرعية · الأشهر المكتملة حتى ${m}`,
    median: "الوسيط", sample: (n) => `${n} صفقة`, conf: { larger: "عيّنة أكبر", limited: "عيّنة محدودة", insufficient: "بيانات غير كافية" },
    month: "الشهر", src: "المصدر: دائرة الأراضي والأملاك · صفقات بيع مسجّلة", approx: "≈ قيمة تقريبية لمنطقة لم تُطابَق بعد مع البيانات الحيّة للدائرة.",
    loading: "جارٍ التحميل…", na: "البيانات غير متاحة مؤقتاً.",
  },
};

const num = (v, isAR) => (isAR ? arDigits(String(v)) : String(v));
export const nm = (a, isAR) => (isAR ? a.area_ar || a.area : a.area);

export function Card({ title, sub, children, testId, right }) {
  return (
    <section className="h-full flex flex-col rounded-[1.5rem] border hairline bg-white/80 p-5 sm:p-6 min-w-0" data-testid={testId}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-editorial text-lg sm:text-xl text-propertic-navy">{title}</h3>
          {sub && <p className="text-[12px] text-propertic-muted mt-1">{sub}</p>}
        </div>
        {right}
      </div>
      <div className="mt-4 flex-1 flex flex-col min-w-0">{children}</div>
    </section>
  );
}

export function Key({ A, B, isAR }) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-propertic-ink-2">
      {[["A", A], ["B", B]].map(([k, a]) => (
        <span key={k} className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-full" style={{ background: COL[k] }} />{nm(a, isAR)}</span>
      ))}
    </div>
  );
}

export function PsfInfo({ tip, dark }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" aria-label={tip} className={`shrink-0 rounded-full ${dark ? "text-white/80" : "text-propertic-muted"} hover:text-propertic-gold`} data-testid="compare-psf-info"><Info className="w-3 h-3" /></button>
      </PopoverTrigger>
      <PopoverContent collisionPadding={12} className="w-[min(288px,calc(100vw-24px))] rounded-2xl text-[12px] leading-relaxed text-propertic-ink" data-testid="compare-psf-info-content">{tip}</PopoverContent>
    </Popover>
  );
}

export function MetricRows({ A, B, isAR, format }) {
  const t = CT[isAR ? "ar" : "en"];
  const pct = (v, sign) => (v == null ? "—" : `${sign && v >= 0 ? "+" : ""}${num(v, isAR)}${isAR ? "٪" : "%"}`);
  const cols = [
    ["psf", t.psf, (x) => x.avg_price_sqft_aed, (v) => fmtPsf(v, isAR)],
    ["yield", t.yield, (x) => x.rental_yield_pct, (v) => pct(v)],
    ["yoy", t.yoy, (x) => x.yoy_change_pct, (v) => pct(v, true)],
    ["rent1", t.rent1, (x) => x.avg_rent_1br_aed, (v) => fmtAED(v, isAR)],
    ["rent2", t.rent2, (x) => x.avg_rent_2br_aed, (v) => fmtAED(v, isAR)],
    ["txns", t.txns, (x) => x.transactions_last_quarter, (v) => N(v, isAR)],
  ];
  return (
    <div className="space-y-3" data-testid="compare-metric-rows">
      {[["A", A, B], ["B", B, A]].map(([k, x, o]) => (
        <div key={k} className="rounded-2xl border hairline bg-white p-3 sm:p-4" data-testid={`compare-row-area-${k.toLowerCase()}`}>
          <div className="flex items-center gap-2 mb-3">
            <span className="w-3 h-3 rounded-full shrink-0" style={{ background: COL[k] }} />
            <span className="font-editorial text-[15px] sm:text-base text-propertic-navy">{nm(x, isAR)}{!x.verified ? " ≈" : ""}</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
            {cols.map(([id, label, get, f]) => {
              const v = get(x);
              const hi = v != null && get(o) != null && v > get(o);
              return (
                <div key={id} className="rounded-xl px-3 py-2 min-w-0" style={hi ? { background: COL.A, color: "#fff" } : { background: "#F6F1E6", color: "#14181F" }} data-testid={`compare-${id}-${k.toLowerCase()}`} data-higher={hi ? "true" : undefined}>
                  <p className={`text-[10.5px] leading-tight flex items-center gap-1 ${hi ? "text-white/75" : "text-propertic-muted"}`}>{label}{id === "yield" && <YieldMethodologyInfo dark={hi} />}{id === "psf" && <PsfInfo tip={t.psfTip} dark={hi} />}</p>
                  <p className="mt-1 font-semibold tabular-nums whitespace-nowrap text-[14px] sm:text-[15px]" dir="ltr">{fixArThousands(f(v))}</p>
                </div>
              );
            })}
          </div>
        </div>
      ))}
      <p className="flex items-center gap-1.5 text-[11px] text-propertic-muted"><span className="w-4 h-3 rounded" style={{ background: COL.A }} />{t.higher}</p>
    </div>
  );
}

export function TrendCompare({ A, B, isAR }) {
  const t = CT[isAR ? "ar" : "en"];
  // Align by canonical month label, never by array position: each series is looked up
  // through its OWN spark_months, so a sparse series cannot shift into another month.
  const byMonth = (S) => {
    const out = {};
    (S.spark_months || []).forEach((m, i) => { const x = (S.spark || [])[i]; out[m] = x == null ? null : typeof x === "object" ? x.v : x; });
    return out;
  };
  const key = (m) => { const r = /^(\d{2}) '(\d{2})$/.exec(m || ""); return r ? `20${r[2]}-${r[1]}` : m; };
  const mA = byMonth(A), mB = byMonth(B);
  const months = [...new Set([...(A.spark_months || []), ...(B.spark_months || [])])].sort((a, b) => key(a).localeCompare(key(b)));
  const label = (m) => { const r = /^(\d{2}) '(\d{2})$/.exec(m || ""); return r ? fmtMonth(`20${r[2]}-${r[1]}`, isAR, true) : m; };
  const data = months.map((m) => ({ m: label(m), A: toCur(mA[m] ?? null), B: toCur(mB[m] ?? null) }));
  return (
    <Card title={t.trend} sub={t.trendSub} testId="compare-trend" right={<Key A={A} B={B} isAR={isAR} />}>
      <div className="flex-1 min-h-[220px]" dir="ltr">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 6, right: 14, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#E4DDCC" vertical={false} />
            <XAxis dataKey="m" tick={AX} reversed={isAR} interval="preserveStartEnd" padding={{ left: 8, right: 14 }} />
            <YAxis tick={AX} width={isAR ? 62 : 44} orientation={isAR ? "right" : "left"} domain={["auto", "auto"]} tickFormatter={(x) => (x < 2000 ? (isAR ? arDigits(Math.round(x)) : Math.round(x)) : isAR ? `${arDigits((x / 1000).toFixed(1))} ألف` : `${(x / 1000).toFixed(1)}k`)} />
            <Tooltip formatter={(x, k) => [Math.round(x).toLocaleString("en"), k === "A" ? nm(A, isAR) : nm(B, isAR)]} />
            <Line type="monotone" dataKey="A" stroke={COL.A} strokeWidth={2.5} dot={{ r: 3, fill: COL.A }} isAnimationActive={false} />
            <Line type="monotone" dataKey="B" stroke={COL.B} strokeWidth={2.5} dot={{ r: 3, fill: COL.B }} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

export function SalesCompare({ A, B, activity, isAR }) {
  const t = CT[isAR ? "ar" : "en"];
  if (!activity) return <Card title={t.sales} testId="compare-sales"><p className="text-sm text-propertic-muted">{activity === false ? t.na : t.loading}</p></Card>;
  const months = activity.months.filter((m) => m !== activity.partial?.month);
  const ra = activity.curated.find((c) => c.market_area === A.area);
  const rb = activity.curated.find((c) => c.market_area === B.area);
  const data = months.map((m) => ({ m: fmtMonth(m, isAR, true), A: ra?.counts[m] ?? null, B: rb?.counts[m] ?? null }));
  return (
    <Card title={t.sales} sub={t.salesSub} testId="compare-sales" right={<Key A={A} B={B} isAR={isAR} />}>
      <div className="flex-1 min-h-[220px]" dir="ltr">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 6, right: 8, left: 0, bottom: 0 }} barGap={2}>
            <CartesianGrid stroke="#E4DDCC" vertical={false} />
            <XAxis dataKey="m" tick={AX} reversed={isAR} interval="preserveStartEnd" padding={{ left: 8, right: 14 }} />
            <YAxis tick={AX} width={40} orientation={isAR ? "right" : "left"} tickFormatter={(x) => num(x, isAR)} />
            <Tooltip formatter={(x, k) => [N(x, isAR), k === "A" ? nm(A, isAR) : nm(B, isAR)]} cursor={{ fill: "rgba(176,133,67,0.08)" }} />
            <Bar dataKey="A" fill={COL.A} radius={[4, 4, 0, 0]} isAnimationActive={false} />
            <Bar dataKey="B" fill={COL.B} radius={[4, 4, 0, 0]} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

function useBoth(path, A, B, extra = {}) {
  const [d, setD] = useState(null);
  const key = JSON.stringify(extra);
  useEffect(() => {
    let alive = true;
    setD(null);
    Promise.all([A, B].map((x) => api.get(path, { params: { market_area: x.area, ...extra } }).then((r) => r.data)))
      .then((v) => { if (alive) setD(v); }).catch(() => { if (alive) setD(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, A.area, B.area, key]);
  return d;
}

export function BandsCompare({ A, B, isAR }) {
  const t = CT[isAR ? "ar" : "en"];
  const d = useBoth("/market/price-bands", A, B);
  const [month, setMonth] = useState(null);
  const months = d ? d[0].months : [];
  const m = month || months[months.length - 1];
  const rows = d && m ? BAND_IDS.map((id, i) => {
    const ba = d[0].by_month[m].bands.find((b) => b.band === id);
    const bb = d[1].by_month[m].bands.find((b) => b.band === id);
    return { name: BAND_LBL[isAR ? "ar" : "en"][i], A: ba.share_pct ?? 0, B: bb.share_pct ?? 0, nA: ba.sales_count, nB: bb.sales_count };
  }) : [];
  const picker = d && <RoundSelect value={m} onChange={setMonth} label={t.month} testId="compare-bands-month" isAR={isAR} className="w-[150px]" options={[...months].reverse().map((x) => ({ value: x, label: fmtMonth(x, isAR) }))} />;
  return (
    <Card title={t.bands} sub={m ? t.bandsSub(fmtMonth(m, isAR)) : ""} testId="compare-bands" right={picker}>
      {d === null && <p className="text-sm text-propertic-muted">{t.loading}</p>}
      {d === false && <p className="text-sm text-propertic-muted">{t.na}</p>}
      {d && (
        <>
          <Key A={A} B={B} isAR={isAR} />
          <div className="mt-3 flex-1 min-h-[220px]" dir="ltr">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={rows} margin={{ top: 18, right: 8, left: 0, bottom: 0 }} barGap={3}>
                <CartesianGrid stroke="#E4DDCC" vertical={false} />
                <XAxis dataKey="name" tick={AX} reversed={isAR} interval={0} />
                <YAxis tick={AX} width={36} orientation={isAR ? "right" : "left"} tickFormatter={(x) => `${num(x, isAR)}${isAR ? "٪" : "%"}`} />
                <Tooltip formatter={(x, k, p) => [`${num(x.toFixed(1), isAR)}${isAR ? "٪" : "%"} · ${N(k === "A" ? p.payload.nA : p.payload.nB, isAR)}`, k === "A" ? nm(A, isAR) : nm(B, isAR)]} cursor={{ fill: "rgba(176,133,67,0.08)" }} />
                <Bar dataKey="A" fill={COL.A} radius={[4, 4, 0, 0]} isAnimationActive={false} label={{ position: "top", fontSize: 9, fill: COL.A, formatter: (x) => num(Math.round(x), isAR) }} />
                <Bar dataKey="B" fill={COL.B} radius={[4, 4, 0, 0]} isAnimationActive={false} label={{ position: "top", fontSize: 9, fill: COL.B, formatter: (x) => num(Math.round(x), isAR) }} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]" data-testid="compare-bands-totals">
            {[["A", A, d[0]], ["B", B, d[1]]].map(([k, x, r]) => (
              <p key={k} className="rounded-xl bg-propertic-paper-2 border hairline px-3 py-2 text-propertic-ink-2">
                <span className="font-semibold" style={{ color: COL[k] }}>{nm(x, isAR)}</span><br />
                {N(r.by_month[m].total_sales_count, isAR)} {isAR ? "صفقة" : "sales"} · {fmtAED(r.by_month[m].total_sales_aed, isAR)}
              </p>
            ))}
          </div>
        </>
      )}
    </Card>
  );
}

export function MovementCompare({ A, B, isAR }) {
  const t = CT[isAR ? "ar" : "en"];
  const d = useBoth("/market/price-movement", A, B, { property_type: "Unit" });
  const last = d && d[0].months[d[0].months.length - 1];
  const data = d ? d[0].series.map((s, i) => ({ m: fmtMonth(s.month, isAR, true), A: toCur(s.median_aed_per_sqft), B: toCur(d[1].series[i].median_aed_per_sqft), rA: s.p25_aed_per_sqft ? [toCur(s.p25_aed_per_sqft), toCur(s.p75_aed_per_sqft)] : null, rB: d[1].series[i].p25_aed_per_sqft ? [toCur(d[1].series[i].p25_aed_per_sqft), toCur(d[1].series[i].p75_aed_per_sqft)] : null })) : [];
  return (
    <Card title={t.move} sub={last ? t.moveSub(fmtMonth(last, isAR)) : ""} testId="compare-movement">
      {d === null && <p className="text-sm text-propertic-muted">{t.loading}</p>}
      {d === false && <p className="text-sm text-propertic-muted">{t.na}</p>}
      {d && (
        <>
          <div className="grid grid-cols-2 gap-2">
            {[["A", A, d[0]], ["B", B, d[1]]].map(([k, x, r]) => {
              const s = r.series[r.series.length - 1];
              return (
                <div key={k} className="rounded-xl bg-propertic-paper-2 border hairline px-3 py-2 min-w-0" data-testid={`compare-movement-${k.toLowerCase()}`}>
                  <p className="text-[11px] font-semibold truncate" style={{ color: COL[k] }}>{nm(x, isAR)}</p>
                  <p className="font-editorial text-xl text-propertic-navy tabular-nums" dir="ltr">{fmtPsf(s.median_aed_per_sqft, isAR)}</p>
                  <p className="text-[10.5px] text-propertic-muted">{fmtPct(s.change_pct, isAR)} · {t.conf[s.confidence]} · {t.sample(N(s.qualifying_sales, isAR))}</p>
                </div>
              );
            })}
          </div>
          <div className="mt-3 flex-1 min-h-[200px]" dir="ltr">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={data} margin={{ top: 6, right: 14, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="#E4DDCC" vertical={false} />
                <XAxis dataKey="m" tick={AX} reversed={isAR} interval="preserveStartEnd" padding={{ left: 8, right: 14 }} />
                <YAxis tick={AX} width={44} orientation={isAR ? "right" : "left"} domain={["auto", "auto"]} tickFormatter={(x) => num(x, isAR)} />
                <Tooltip formatter={(x, k) => (Array.isArray(x) ? [`${Math.round(x[0])} – ${Math.round(x[1])}`, "P25–P75"] : [Math.round(x), k === "A" ? nm(A, isAR) : nm(B, isAR)])} />
                <Area type="monotone" dataKey="rA" stroke="none" fill={COL.A} fillOpacity={0.08} isAnimationActive={false} />
                <Area type="monotone" dataKey="rB" stroke="none" fill={COL.B} fillOpacity={0.14} isAnimationActive={false} />
                <Line type="monotone" dataKey="A" stroke={COL.A} strokeWidth={2.5} dot={{ r: 3, fill: COL.A }} isAnimationActive={false} />
                <Line type="monotone" dataKey="B" stroke={COL.B} strokeWidth={2.5} dot={{ r: 3, fill: COL.B }} isAnimationActive={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <p className="mt-2 text-[11px] text-propertic-muted">{isAR ? "الخط: الوسيط · المنطقة المظلّلة: النصف الأوسط من الأسعار. لا يُعرض الوسيط إذا قلّت الصفقات المؤهلة عن ٢٠." : "Line: median · shaded: middle 50% of prices. No median is shown under 20 qualifying sales."}</p>
        </>
      )}
    </Card>
  );
}
